<template>
  <div class="h-100pc">
    <v-row
      :class="$vuetify.smAndUp ? 'px-10' : 'px-5'"
      :style="heroStyle"
      class="pt-12 pb-0"
    >
      <v-col>
        <v-row class="mb-6">
          <v-col>
            <h1 :class="introClass">
              {{ $bct(store.state.pages.Welcome.intro) }}
            </h1>
          </v-col>
        </v-row>

        <v-row v-if="searchFlags.regionSearchEnabled">
          <v-col class="mxw-1800 mx-auto">
            <v-row class="d-flex justify-center">
              <v-col
                cols="12"
                lg="6"
                md="8"
              >
                <v-card class="py-4 px-6 rounded-pill d-flex align-center mb-16">
                  <v-text-field
                    v-model="search.unformatted_origin"
                    @keyup.enter="submitDisabled ? false : submit()"
                    :class="$vuetify.display.smAndUp ? 'fs-22' : 'fs-16'"
                    :label="$bct('Search by address')"
                    class="mt-0 pt-0"
                    hide-details
                  />
                  <v-btn
                    @click="submit"
                    :disabled="submitDisabled"
                    :loading="processing"
                    :ripple="false"
                    :size="$vuetify.display.smAndUp ? 'x-large' : undefined"
                    :style="submitDisabled ? DISABLED_SUBMIT_STYLE : ''"
                    class="focus-very-visible"
                    color="primary"
                    data-cy="submit_welcome"
                    prepend-icon="search"
                    rounded
                  >
                    <span>{{ $bct('Submit') }}</span>
                  </v-btn>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <template v-else>
          <v-row v-if="$vuetify.display.mdAndUp">
            <v-col class="mxw-1200 mx-auto">
              <v-row class="d-flex justify-center">
                <v-col
                  cols="12"
                  md="4"
                >
                  <LocationMenu
                    @keyboard.tab="$refs.travel_mode_menu.focus()"
                    ref="locationMenu"
                    :autofocus="false"
                    :search="search"
                  />
                </v-col>

                <v-col
                  v-if="searchFlags.transportationOptionsEnabled"
                  cols="12"
                  md="4"
                >
                  <TravelModeMenu
                    v-model="search.travel_mode"
                    ref="travel_mode_menu"
                    :travel-mode="selectedTravelMode"
                  />
                </v-col>

                <v-col
                  v-if="searchFlags.dobSearchEnabled"
                  cols="12"
                  md="4"
                >
                  <ChildrenMenuV2
                    @on-add-child="(child) => handleAddChild(child)"
                    @on-remove-child="(index) => handleRemoveChild(index)"
                    @on-update-child="(child, index) => handleChildrenOverride(child, index)"
                    :children="children"
                  />
                </v-col>

                <v-col
                  v-else-if="!searchFlags.defaultAgeGroupEnabled"
                  cols="12"
                  md="4"
                >
                  <ChildrenMenu
                    @decrease="modifyCount($event, -1)"
                    @increase="modifyCount($event, 1)"
                    :age-groups="ageGroups"
                    :sum="sum"
                  />
                </v-col>

                <v-col
                  class="mb-4 ta-center"
                  cols="12"
                >
                  <v-btn
                    @click="submit"
                    :disabled="submitDisabled"
                    :loading="processing"
                    :ripple="false"
                    :style="submitDisabled ? DISABLED_SUBMIT_STYLE : ''"
                    class="focus-very-visible mx-auto w-300"
                    color="primary"
                    data-cy="submit_welcome"
                    prepend-icon="search"
                    size="x-large"
                    rounded
                  >
                    <span>{{ $bct('Submit') }}</span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row v-else>
            <v-col>
              <v-card class="py-0 mb-4">
                <v-list>
                  <v-list-item
                    @click="openSearchDialog('location')"
                    class="py-1"
                  >
                    <template #prepend>
                      <v-icon
                        color="primary"
                        icon="location_on"
                      />
                    </template>
                    <v-list-item-title>
                      <span v-text="search.formatted_origin || $bct('Location')" />
                    </v-list-item-title>
                    <template #append>
                      <v-btn
                        :ripple="false"
                        aria-label="Click to expand more"
                        icon="expand_more"
                        size="x-small"
                        variant="text"
                      />
                    </template>
                  </v-list-item>
                  <template v-if="searchFlags.transportationOptionsEnabled">
                    <v-divider class="mx-2" />
                    <v-list-item
                      @click="openSearchDialog('mode')"
                      class="py-1"
                    >
                      <template #prepend>
                        <v-icon
                          color="primary"
                          icon="directions_car"
                        />
                      </template>
                      <v-list-item-title>{{ $bct(selectedTravelMode) }}</v-list-item-title>
                      <template #append>
                        <v-btn
                          :ripple="false"
                          aria-label="Click to expand more"
                          icon="expand_more"
                          size="x-small"
                          variant="text"
                        />
                      </template>
                    </v-list-item>
                  </template>
                  <template v-if="!searchFlags.defaultAgeGroupEnabled">
                    <v-divider class="mx-2" />
                    <v-list-item
                      @click="openSearchDialog('children')"
                      class="py-1"
                    >
                      <template #prepend>
                        <v-icon
                          color="primary"
                          icon="person_outline"
                        />
                      </template>
                      <v-list-item-title>
                        {{ sum > 0 ? [sum, $bct('children')].join(' ') : $bct('Children ages') }}
                      </v-list-item-title>
                      <template #append>
                        <v-btn
                          :ripple="false"
                          aria-label="Click to expand more"
                          icon="expand_more"
                          size="x-small"
                          variant="text"
                        />
                      </template>
                    </v-list-item>
                  </template>
                  <v-divider class="mx-2" />
                </v-list>
                <div class="pa-2">
                  <v-btn
                    @click="submit"
                    :disabled="submitDisabled"
                    :loading="processing"
                    :ripple="false"
                    :style="submitDisabled ? DISABLED_SUBMIT_STYLE : ''"
                    class="w-100pc"
                    color="primary"
                    prepend-icon="search"
                    size="x-large"
                    rounded
                  >
                    {{ $bct('Submit') }}
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>

    <template
      v-if="
        store.state.pages.Welcome.sponsor.name ||
        store.state.pages.Welcome.disclaimers.length > 0 ||
        store.state.pages.Welcome.partners.length > 0
      "
    >
      <v-row class="bg-white py-8 px-4">
        <v-col
          v-if="store.state.pages.Welcome.sponsor.name"
          class="pa-6"
          cols="12"
        >
          <div class="c-black fs-16 fw-500 mb-4 ta-center">
            {{ $bct(store.state.pages.Welcome.sponsor.title) }}
          </div>
          <div class="d-flex align-start mxw-1000 mx-auto">
            <img
              :alt="`${$bct('Sponsored by')} ${store.state.pages.Welcome.sponsor.name}`"
              :src="store.state.pages.Welcome.sponsor.image"
              class="me-4"
              height="40"
            />
            <div class="fs-16 c-light-black">
              {{ $bct(store.state.pages.Welcome.sponsor.text) }}
            </div>
          </div>
        </v-col>
        <v-col
          v-if="store.state.pages.Welcome.disclaimers.length > 0"
          class="pa-6"
          cols="12"
        >
          <div class="mxw-1000 mx-auto">
            <MarkdownContent
              v-for="(item, index) in store.state.pages.Welcome.disclaimers"
              :key="index"
              :content="item.text"
              class="c-light-black fs-16"
            />
          </div>
        </v-col>
        <v-col
          v-if="store.state.pages.Welcome.partners.length > 0"
          cols="12"
        >
          <PartnerRoll
            :partners="store.state.pages.Welcome.partners"
            :title="store.state.pages.Welcome.partnership_title"
          />
        </v-col>
      </v-row>
    </template>
    <template
      v-if="store.state.pages.Welcome.programs && store.state.pages.Welcome.programs.length > 0"
    >
      <v-row class="bg-super-light-blue">
        <v-col class="mxw-1800 mx-auto">
          <IconList :icons="store.state.pages.Welcome.programs" />
        </v-col>
      </v-row>
    </template>
    <template v-if="store.state.pages.Welcome.highlights.length > 0">
      <v-row
        v-for="(feature, index) in featureHighlights"
        :key="index"
        class="bg-white pt-6 pb-12 px-6 px-lg-12"
      >
        <v-col class="mxw-1800 mx-auto px-lg-12 px-6">
          <FeatureHighlight
            :img="feature.img"
            :reverse="feature.reverse"
            :subtext="feature.subtext"
            :text="feature.text"
          />
        </v-col>
      </v-row>
    </template>
    <MobileSearchDialog
      @change="search = $event"
      @change:count="modifyCount($event[0], $event[1])"
      @on-add-child="(child) => handleAddChild(child)"
      @on-remove-child="(index) => handleRemoveChild(index)"
      @on-update-child="(child, index) => handleChildrenOverride(child, index)"
      ref="mobileSearchDialog"
      :age-groups="ageGroups"
      :children="children"
    />
    <WelcomeFooter />
  </div>
</template>

<script setup>
import ChildrenMenu from '@/parent/components/children/ChildrenMenu.vue';
import ChildrenMenuV2 from '@/parent/components/children/ChildrenMenuV2.vue';
import FeatureHighlight from '@/shared/components/content/FeatureHighlight.vue';
import IconList from '@/shared/components/content/IconList.vue';
import LocationMenu from '@/shared/components/form/LocationMenu.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import MobileSearchDialog from '@/parent/components/searches/MobileSearchDialog.vue';
import ParentSearchParams from '@/parent/services/parent_search_params';
import PartnerRoll from '@/shared/components/content/PartnerRoll.vue';
import SearchFlags from '@/parent/services/search-flags';
import TravelModeMenu from '@/shared/components/form/TravelModeMenu.vue';
import WelcomeFooter from '@/shared/components/WelcomeFooter.vue';

import useApi from '@/shared/composables/useApi';
import useAssets from '@/shared/composables/useAssets';
import { useDisplay } from 'vuetify';
import useEventBus from '@/shared/composables/useEventBus';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

const DEFAULT_HIGHLIGHTS = [
  {
    text: 'Find all types of child care, all in one place',
    subtext:
      "Explore physical child care programs based on your location and children's age. Or, explore virtual programs!",
  },
  {
    text: 'Filter to find child care providers that have openings right now',
    subtext:
      'Providers are regularly updating information to showcase available openings. Quickly find providers accepting new families.',
  },
  {
    text: 'Save and create a list of your favorite child care options',
    subtext:
      'Create an account to save your list of favorite child care providers. All you need is your name and email address.',
  },
];
const DISABLED_SUBMIT_STYLE = 'background-color: #D8D8D8 !important; color: white !important;';

const { api } = useApi();
const { assets } = useAssets();
const display = useDisplay();
const eventBus = useEventBus();
const { locale } = useI18n();
const router = useRouter();
const store = useStore();
const $bct = inject('translator');

const searchFlags = ref(new SearchFlags(store));
const children = ref(
  searchFlags.value.dobSearchEnabled ? [{ dob: null, care_needed_date: null }] : null,
);

const ageGroups = ref(searchFlags.value.ageGroups);
const locationMenu = ref(false);
const mobileSearchDialog = ref(null);
const parent = ref({
  is_anonymous: true,
  is_parent: true,
  organization_id: window.organization_id,
});
const search = ref({
  destination: {},
  exclude_closed: searchFlags.value.closedFilterEnabled,
  location_type: 'home',
  travel_mode: 'DRIVING',
  formatted_origin: null,
  origin: {},
  schema_id: getProviderSchemaId(),
  unformatted_origin: null,
  zip: null,
});
const processing = ref(false);

const introClass = computed(() => {
  if (display.mdAndUp.value) {
    return 'fs-40 fw-900 lh-52 c-white mxw-800 mx-auto ta-center';
  }
  return 'fs-24 fw-600 lh-30 c-white mxw-800 mx-auto ta-center';
});

const childrenDoneDisabled = computed(() => {
  if (searchFlags.value.regionSearchEnabled) return false;

  let count = 0;

  if (searchFlags.value.dobSearchEnabled) {
    count = children.value?.filter(
      (child) => child.dob !== null && child.care_needed_date !== null,
    ).length;
  } else {
    count = ageGroups.value?.filter((ageGroup) => ageGroup.count > 0).length;
  }

  return count === 0;
});

// TODO : CHANGE TO COMPUTED
const featureHighlights = computed(() => {
  return store.state.pages.Welcome.highlights.map((highlight, index) => ({
    img: highlight.img,
    text: highlight.text || DEFAULT_HIGHLIGHTS[0].text,
    subtext: highlight.subtext || DEFAULT_HIGHLIGHTS[0].subtext,
    reverse: index % 2 !== 0,
  }));
});

const heroStyle = computed(() => {
  if (store.state.pages.Welcome.hero_style) {
    return store.state.pages.Welcome.hero_style;
  }

  return `background: linear-gradient(to top right, rgba(20, 54, 82, 0.8), rgba(20, 54, 82, 0.8)), url(${store.state.pages.Welcome.hero_image}) center center no-repeat;`;
});

const selectedTravelMode = computed(() => {
  return (
    assets.transit_options.find((option) => option.value === search.value.travel_mode).text ||
    'Transportation method'
  );
});

const submitDisabled = computed(() => {
  return (
    childrenDoneDisabled.value ||
    !search.value.travel_mode ||
    (!search.value.unformatted_origin && !search.value.formatted_origin)
  );
});

const sum = computed(() => {
  if (searchFlags.value.dobSearchEnabled) {
    return children.value.filter((child) => child.dob !== null && child.care_needed_date !== null)
      .length;
  }

  return ageGroups.value.reduce((total, ageGroup) => total + ageGroup.count, 0);
});

function handleAddChild(child) {
  // We need to duplicate the child object otherwise the equality check on remove has false positive
  children.value.push({ ...child });
}

function handleRemoveChild(index) {
  children.value = children.value.filter((child) => child !== children.value[index]);
}

function handleChildrenOverride(child, index) {
  children.value.splice(index, 1, child);
}

function getChildren() {
  let childrenResult = ageGroups.value?.filter((ageGroup) => ageGroup.count > 0) || [];

  if (searchFlags.value.regionSearchEnabled && !searchFlags.value.defaultAgeGroupEnabled) {
    childrenResult = [];
  } else if (searchFlags.value.dobSearchEnabled) {
    childrenResult = children.value;
  }

  return childrenResult;
}

function getProviderSchemaId() {
  const schema = Object.values(store.state.schemas).filter(
    (schema) => schema.data_type === 'Provider',
  )[0];

  return schema?.id || null;
}

function modifyCount(index, addition) {
  ageGroups.value[index].count += addition;
}

function openSearchDialog(mode) {
  mobileSearchDialog.value?.open(mode, search.value);
}

function submit() {
  processing.value = true;
  store.dispatch('identify', {
    success: submitSearch,
    failure: submitAndCreateMember,
  });
}

function submitSearch() {
  const params = new ParentSearchParams(parent.value, search.value, getChildren()).asJson();

  api.search.create(
    params.search,
    (resp) => {
      store.commit('setSearch', resp.data);
      router.push({ name: 'SearchShow', params: { searchId: resp.data.id } });
    },
    (err) => {
      if (err.response && err.response.data && err.response.data.errors) {
        eventBus.chime(err.response.data.errors[0]);
      } else {
        eventBus.chime('Unknown error occurred');
      }
      processing.value = false;
    },
  );
}

function submitAndCreateMember() {
  parent.value.default_locale = locale.value;

  const params = new ParentSearchParams(parent.value, search.value, getChildren()).asJson();

  api.member.create(
    params,
    (resp) => {
      store.dispatch('identify', {
        profile: resp.data,
        success() {
          submitSearch();
        },
      });
    },
    (err) => {
      eventBus.error(err);
      processing.value = false;
    },
  );
}

onMounted(() => {
  store.dispatch('identify');
});
</script>

<style scoped>
.bg-hero {
  background-size: cover;
}

.lh-30 {
  line-height: 30px;
}

.mxw-1000 {
  max-width: 1000px;
}
</style>
